import { useState } from 'react';
import { router } from '@inertiajs/react';
import { listTilesByDomain, getTileUrl, type Domain } from '@/components/HexMap/tiles';

interface Props {
    hexId: number;
    currentDomain?: Domain;
    currentTileImage?: string;
    onClose: () => void;
}

const DOMAIN_OPTIONS: Array<{ value: Domain; label: string; emoji: string }> = [
    { value: 'deadlands', label: 'Deadlands', emoji: '💀' },
    { value: 'drylands', label: 'Drylands', emoji: '🌵' },
    { value: 'greenlands', label: 'Greenlands', emoji: '🌿' },
    { value: 'icelands', label: 'Icelands', emoji: '❄️' },
    { value: 'sandlands', label: 'Sandlands', emoji: '🏜️' },
];

/**
 * Modal for changing a hex's tile image.
 * Allows selecting a domain and then a specific tile within that domain.
 */
export function TileSelectionModal({ hexId, currentDomain, currentTileImage, onClose }: Props) {
    const [step, setStep] = useState<1 | 2>(1);
    const [selectedDomain, setSelectedDomain] = useState<Domain | ''>(currentDomain || '');
    const [selectedTile, setSelectedTile] = useState<string>(currentTileImage || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleDomainSelect = (domain: Domain) => {
        setSelectedDomain(domain);
        // If changing domain, reset tile selection
        if (domain !== currentDomain) {
            setSelectedTile('');
        }
        setStep(2);
    };

    const handleTileSelect = (filename: string) => {
        setSelectedTile(filename);
    };

    const handleSubmit = () => {
        if (!selectedDomain || !selectedTile) return;

        setIsSubmitting(true);
        setError(null);

        router.patch(`/hex/${hexId}/tile`, {
            domain: selectedDomain,
            tile_image: selectedTile,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                onClose();
            },
            onError: (errors) => {
                setError(errors.domain || errors.tile_image || 'Failed to update tile image.');
                setIsSubmitting(false);
            },
        });
    };

    const tiles = selectedDomain ? listTilesByDomain(selectedDomain) : [];
    const previewUrl = selectedDomain && selectedTile ? getTileUrl(selectedDomain, selectedTile) : null;

    return (
        <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={onClose}
        >
            <div
                className="bg-slate-800 rounded-lg w-full max-w-xl max-h-[90vh] overflow-hidden border border-slate-700"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
                    <h2 className="text-lg font-semibold text-white">Change Tile Image</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors p-1"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                    {/* Error message */}
                    {error && (
                        <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg">
                            <p className="text-red-300 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Step 1: Domain Selection */}
                    {step === 1 && (
                        <div>
                            <p className="text-sm text-gray-400 mb-4">
                                Select a domain for your hex tile:
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {DOMAIN_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => handleDomainSelect(option.value)}
                                        className={`p-4 rounded-lg border transition-colors ${
                                            selectedDomain === option.value
                                                ? 'border-purple-500 bg-purple-900/30'
                                                : 'border-slate-600 bg-slate-700/50 hover:bg-slate-700'
                                        }`}
                                    >
                                        <div className="text-2xl text-center mb-1">{option.emoji}</div>
                                        <span className="block text-sm text-gray-300 text-center">
                                            {option.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 2: Tile Selection */}
                    {step === 2 && selectedDomain && (
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <p className="text-sm text-gray-400">
                                    Choose a tile from{' '}
                                    <span className="text-white capitalize">{selectedDomain}</span>:
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="text-sm text-purple-400 hover:text-purple-300"
                                >
                                    ← Change domain
                                </button>
                            </div>

                            {tiles.length === 0 ? (
                                <p className="text-gray-500 text-sm italic">
                                    No tiles available for this domain.
                                </p>
                            ) : (
                                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto pr-1">
                                    {tiles.map(({ filename, url }) => (
                                        <button
                                            key={filename}
                                            type="button"
                                            onClick={() => handleTileSelect(filename)}
                                            className={`relative rounded-lg border overflow-hidden transition-all ${
                                                selectedTile === filename
                                                    ? 'border-purple-500 ring-2 ring-purple-500/40'
                                                    : 'border-slate-600 hover:border-slate-500'
                                            }`}
                                            title={filename}
                                        >
                                            <img
                                                src={url}
                                                alt={filename}
                                                className="w-full aspect-square object-cover"
                                            />
                                            {selectedTile === filename && (
                                                <div className="absolute inset-0 bg-purple-500/20 flex items-center justify-center">
                                                    <svg className="w-6 h-6 text-purple-300" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Preview */}
                            {selectedTile && previewUrl && (
                                <div className="mt-4 p-3 bg-slate-900 rounded-lg">
                                    <p className="text-xs text-gray-400 mb-2">Selected tile:</p>
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={previewUrl}
                                            alt={selectedTile}
                                            className="w-16 h-16 rounded border border-slate-600 object-cover"
                                        />
                                        <div className="text-sm text-gray-300">
                                            <p className="font-medium capitalize">{selectedDomain}</p>
                                            <p className="text-gray-500 text-xs truncate max-w-[200px]">
                                                {selectedTile}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-slate-700 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!selectedDomain || !selectedTile || isSubmitting}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                    >
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TileSelectionModal;
